import axios from 'axios'
import { useEffect } from 'react'
import { Router } from 'react-router-dom'

import { Auth } from '@scouts/auth'
import {
    getIsMobileAppMode,
    mixpanelInit,
    useInitFeatureFlags,
    useSetTitle,
    ChunkLoadErrorBoundary,
    Locale,
} from '@taxfix/scouts-helpers'
import { TranslationProvider } from '@taxfix/scouts-i18n'
import { SingularProvider } from '@scouts/singular'
import { colors, GlobalSpinner, Toaster, TooltipProvider } from '@taxfix/scouts-ui'

import config from '@/config'
import { initSentry } from '@/helpers/sentry'
import history from '@/history'
import { handleSingularError, shouldInitializeSingularWeb, singularConfig } from '@/singular'

import { useAuthConfig } from './auth/hooks/use-auth-config'
import { useTaxfixAnnouncementModal } from './header/hooks'

import { AppRoutes } from './AppRoutes'
import { ChunkLoadError } from './ChunkLoadError'
import { CookieConsent } from './CookieConsent'
import ErrorBoundary from './ErrorBoundary'
import { TaxfixAnnouncementModal } from './header/TaxfixAnnouncementModal'
import { InterceptorProvider } from './InterceptorProvider'
import { LocationObserver } from './LocationObserver'
import { MobileVersionUpdateModal } from './MobileVersionUpdateModal'
import { RequestObserver } from './RequestObserver'

// Move this to a separate file
axios.defaults.baseURL = config.apiUrl

export const App = () => {
    const { isLoading } = useInitFeatureFlags()
    const isMobileAppMode = getIsMobileAppMode()

    const {
        closeTaxfixAnnouncementModal,
        isTaxfixAnnouncementModalOpen,
        hasSeenTaxfixAnnouncement,
        openTaxfixAnnouncementModal,
    } = useTaxfixAnnouncementModal()

    useSetTitle('Taxfix (formerly TaxScouts)')

    const authConfig = useAuthConfig()

    useEffect(() => {
        initSentry()

        if (config.isMixpanelEnabled) {
            mixpanelInit({ token: config.mixpanelToken })
        }
    }, [])

    // Temporary
    useEffect(() => {
        if (!isTaxfixAnnouncementModalOpen && !hasSeenTaxfixAnnouncement && isMobileAppMode) {
            openTaxfixAnnouncementModal()
        }
    }, [isTaxfixAnnouncementModalOpen, hasSeenTaxfixAnnouncement, isMobileAppMode, openTaxfixAnnouncementModal])

    if (isLoading) {
        return <GlobalSpinner color={colors.surface.attention} />
    }

    return (
        <ErrorBoundary>
            <SingularProvider
                config={singularConfig}
                onError={handleSingularError}
                autoInit={shouldInitializeSingularWeb()}
            >
                <MobileVersionUpdateModal mobileAppTargetVersion={config.mobileAppTargetVersion} />
                <TranslationProvider locale={Locale.GB}>
                    <Auth.Provider config={authConfig}>
                        <InterceptorProvider>
                            <Router history={history}>
                                <RequestObserver />
                                <LocationObserver>
                                    <ChunkLoadErrorBoundary renderError={() => <ChunkLoadError />}>
                                        {isTaxfixAnnouncementModalOpen && (
                                            <TaxfixAnnouncementModal onClose={closeTaxfixAnnouncementModal} />
                                        )}
                                        <CookieConsent />
                                        <Toaster />
                                        <TooltipProvider />
                                        <AppRoutes />
                                    </ChunkLoadErrorBoundary>
                                </LocationObserver>
                            </Router>
                        </InterceptorProvider>
                    </Auth.Provider>
                </TranslationProvider>
            </SingularProvider>
        </ErrorBoundary>
    )
}
